function asList(value) {
	if (!value) return [];
	if (Array.isArray(value)) {
		const nested = value.some(v => v == null || Array.isArray(v) || (v && Array.isArray(v.data)));
		if (nested) {
			return value.flatMap(asList);
		}
		return value;
	}
	if (Array.isArray(value.data)) return value.data;
	return [];
}

/** Flat attachment list helper for badge / count derivation. */
export function asAttachmentList(value) {
	return asList(value);
}

function combineLists(...values) {
	return values.flatMap(asList);
}

function isDeleted(item) {
	return item.deleted === true || item.deleted === 'true' || item.deleted === 1 || item.deleted === '1';
}

function labelOf(item) {
	const name = item.name && String(item.name).trim();
	if (name) return name;
	const labelItem = item.label_item_1 && String(item.label_item_1).trim();
	if (labelItem) return labelItem;
	if (item.description && String(item.description).trim()) return String(item.description).trim();
	return `Work Item #${item.id}`;
}

function categoryOf(item) {
	return String(item.category || item.classification || '').toLowerCase();
}

function descriptionOf(item) {
	const desc = item.description && String(item.description).trim();
	if (desc && desc !== labelOf(item)) return desc;
	return '';
}

function isFlagged(value) {
	return value === true || value === 'true' || value === 1 || value === '1';
}

const MPI_ON_WORK_ITEMS = ['approved', 'completed'];

function tabVisibleForDealership(tab, currentDealership) {
	if (!tab || !tab.active) return false;
	const dealershipField = tab.dealership || '';
	if (!dealershipField.trim()) return true;
	if (!currentDealership) return true;
	return dealershipField.split(',').map(d => d.trim()).includes(currentDealership);
}

/** Active $ / Work Items tab for this dealership (matches vehicle icon grid). */
export function resolveDollarWorkItemsTab(allTabs, currentDealership) {
	const tabs = asList(allTabs).filter(tab => tabVisibleForDealership(tab, currentDealership));
	const byDollarIcon = tabs.find(tab => /fa-dollar|dollar-sign/i.test(String(tab.icon || '')));
	if (byDollarIcon) return byDollarIcon;
	return tabs.find(tab => [
		'tab_new_work_items',
		'tab_work_items',
		'tab_estimate',
	].includes(tab.name)) || null;
}

function appraisalEstimateOptions(appraisals, vehicleId) {
	// Mirror NewAppraisals ($ tab): exclude expense rows.
	const list = combineLists(appraisals).filter(item => item && item.ro_status !== 'expense');
	return toOptions(list, 'appraisal', vehicleId);
}

function mpiOnWorkItemsOptions(mpiWorkItems, vehicleId) {
	// Mirror WorkItemsForm ($ / tools tab): approved/completed or flagged include_in_work_items.
	const list = combineLists(mpiWorkItems).filter(item => item
		&& (MPI_ON_WORK_ITEMS.includes(item.ro_status) || isFlagged(item.include_in_work_items)));
	return toOptions(list, 'mpi', vehicleId);
}

/**
 * Options for "Work Items on This Vehicle" — same set as the $ tab for this dealer.
 * Falls back to appraisal estimates (NewAppraisals behavior) when no $ tab is configured.
 */
export function dollarTabWorkItemSelectOptions({
	allTabs,
	currentDealership,
	mpiWorkItems,
	appraisals,
	workItems,
	vehicleId,
} = {}) {
	const tab = resolveDollarWorkItemsTab(allTabs, currentDealership);
	const name = tab && tab.name;

	if (name === 'tab_work_items') {
		return mpiOnWorkItemsOptions(mpiWorkItems, vehicleId)
			.sort((a, b) => a.label.localeCompare(b.label));
	}

	if (name === 'tab_new_work_items' || name === 'tab_estimate' || !name) {
		return appraisalEstimateOptions(appraisals, vehicleId)
			.sort((a, b) => a.label.localeCompare(b.label));
	}

	// Unknown $ tab — keep prior combined behavior as a safe fallback.
	return workItemSelectOptions({
		mpiWorkItems,
		appraisals,
		workItems,
		vehicleId,
	});
}

/** Dot color for work-item category chips (matches files-tile-mock-v2). */
export function workItemCategoryColor(category) {
	const map = {
		mechanical: '#244aff',
		cosmetic: '#7c4fc9',
		detail: '#0bc2ed',
		body: '#d9772d',
	};
	return map[String(category || '').toLowerCase()] || '#6b7280';
}

function toOptions(list, kind, vehicleId) {
	const byId = new Map();
	asList(list).forEach((item) => {
		if (!item || item.id == null || isDeleted(item)) return;
		if (vehicleId != null && item.vehicle_id != null && String(item.vehicle_id) !== String(vehicleId)) {
			return;
		}
		byId.set(String(item.id), item);
	});
	return [...byId.values()].map(item => ({
		value: `${kind}:${item.id}`,
		label: labelOf(item),
		description: descriptionOf(item),
		category: categoryOf(item),
	}));
}

export function selectedWorkItemValue(attachment) {
	if (!attachment) return '';
	if (attachment.mpi_work_item_id != null && attachment.mpi_work_item_id !== '') {
		return `mpi:${attachment.mpi_work_item_id}`;
	}
	if (attachment.appraisal_id != null && attachment.appraisal_id !== '') {
		return `appraisal:${attachment.appraisal_id}`;
	}
	if (attachment.work_item_id != null && attachment.work_item_id !== '') {
		return `legacy:${attachment.work_item_id}`;
	}
	return '';
}

export function workItemPatchFromValue(value) {
	if (!value) {
		return { mpi_work_item_id: null, appraisal_id: null, work_item_id: null };
	}
	const sep = value.indexOf(':');
	const kind = value.slice(0, sep);
	const id = parseInt(value.slice(sep + 1), 10);
	if (kind === 'mpi') {
		return { mpi_work_item_id: id, appraisal_id: null, work_item_id: null };
	}
	if (kind === 'appraisal') {
		return { mpi_work_item_id: null, appraisal_id: id, work_item_id: null };
	}
	if (kind === 'legacy') {
		return { mpi_work_item_id: null, appraisal_id: null, work_item_id: id };
	}
	return { mpi_work_item_id: null, appraisal_id: null, work_item_id: null };
}

/** Full combined list (MPI + appraisals + legacy) — used to resolve labels for already-linked files. */
export function workItemSelectOptions({
	mpiWorkItems,
	appraisals,
	workItems,
	vehicleId,
} = {}) {
	return [
		...toOptions(combineLists(mpiWorkItems), 'mpi', vehicleId),
		...toOptions(combineLists(appraisals), 'appraisal', vehicleId),
		...toOptions(combineLists(workItems), 'legacy', vehicleId),
	].sort((a, b) => a.label.localeCompare(b.label));
}
